import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useTranslate } from "@tolgee/react";

// Polls /api/version and compares to the sha first observed on this page load.
// When the backend ships a newer image, shows a persistent toast prompting reload.
// Harmless before PR 5 rolls out (endpoint returns "dev" — all matches).
export function useVersionDrift(intervalMs = 60_000) {
  const { t } = useTranslate();
  const initialSha = useRef<string | null>(null);
  const notified = useRef(false);

  useEffect(() => {
    // StrictMode double-invoke safety: reset on mount so remount starts fresh.
    initialSha.current = null;
    notified.current = false;
    let cancelled = false;

    async function check() {
      try {
        const r = await fetch("/api/version", { cache: "no-store" });
        if (!r.ok) return;
        const { sha } = (await r.json()) as { sha?: string };
        if (!sha || cancelled) return;
        if (initialSha.current === null) {
          initialSha.current = sha;
          return;
        }
        if (sha !== initialSha.current && !notified.current) {
          // Fire once per page load. Even if the user dismisses the toast or
          // a third deploy lands, we don't re-nag — the user has seen the
          // notice and will reload on their own terms. Reloading is the only
          // path that fully resets state.
          notified.current = true;
          toast(t("system_version_drift.title"), {
            description: t("system_version_drift.description"),
            duration: Infinity,
            action: {
              label: t("system_version_drift.action"),
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        // network blip — try again next tick
      }
    }

    check();
    const id = setInterval(check, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [intervalMs, t]);
}
